import { SliderModal } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { priorityToSeverity } from '../../../utils/timetable/severity';
import { solverEngineLabelKey } from '../../../utils/timetable/solverDiagnostics';
import { useTimetableStore } from '../TimetableProvider';
import { WizardStep1Preflight } from './WizardStep1Preflight';
import { WizardStep2Options } from './WizardStep2Options';
import styles from './wizard.module.scss';

export const SolverWizardModal: FC = observer(() => {
  const { t } = useTranslation();
  const pinboardStore = useTimetableStore();
  const solver = pinboardStore.solverStore;
  const task = solver.task;
  const isGenerate = task === 'GENERATE';
  const step = solver.wizardStep;
  const jobRunning = solver.jobRunning;
  const lockDismiss = isGenerate && step === 1 && solver.preflightLoading;

  // Step 1 found blockers or could not run: going on is allowed, but say so on the button.
  const preflightBlocked =
    solver.preflightError !== null ||
    (solver.preflightResult ?? []).some((warning) => priorityToSeverity(warning.priority) === 'error');
  const nextText =
    isGenerate && step === 1 && preflightBlocked ? t('pinboard.generate.wizard.continueAnyway') : t('common.next');

  const engineSummary = t(solverEngineLabelKey(solver.selectedEngine));
  const cardCount = pinboardStore.solverChangeableCardCount;

  // GENERATE keeps the 2-step wizard (preflight + options); OPTIMIZE/ASSIGN_ROOMS need no preflight
  // and show a single options slide with their own title/submit wording (ASSIGN_ROOMS has no subtitle).
  // Keys stay static so i18next-parser can extract them — no `t(variable)`.
  const title =
    task === 'OPTIMIZE'
      ? t('pinboard.optimize.wizard.title')
      : task === 'ASSIGN_ROOMS'
        ? t('pinboard.assignRooms.wizard.title')
        : t('pinboard.generate.wizard.title');
  // Untertitel nur beim Optimieren; Generieren und Raumzuordnung erklären sich über ihre Schritte selbst.
  const subtitle = task === 'OPTIMIZE' ? t('pinboard.optimize.wizard.subtitle') : undefined;
  const submitText =
    task === 'OPTIMIZE'
      ? t('pinboard.optimize.wizard.start')
      : task === 'ASSIGN_ROOMS'
        ? t('pinboard.assignRooms.wizard.start')
        : t('pinboard.generate.wizard.step2.startSolver');

  return (
    <SliderModal
      isOpen={solver.solverWizardOpen}
      title={title}
      slides={isGenerate ? [<WizardStep1Preflight />, <WizardStep2Options />] : [<WizardStep2Options />]}
      defaultIndex={isGenerate ? solver.wizardStep - 1 : 0}
      onClose={() => solver.setSolverWizardOpen(false)}
      onPrevious={() => solver.prevWizardStep()}
      onNext={() => solver.nextWizardStep()}
      onSubmit={() => void pinboardStore.generate()}
      nextDisabled={solver.preflightLoading}
      submitDisabled={isGenerate ? step !== 2 || jobRunning : jobRunning}
      loading={isGenerate && solver.preflightLoading}
      loadingLabel={t('pinboard.generate.wizard.checking')}
      nextText={nextText}
      submitText={submitText}
      additionalLeftComponent={
        isGenerate && step === 2 ? (
          <span className={styles['footer-summary']}>
            {t('pinboard.generate.wizard.step2.summary', { engine: engineSummary, count: cardCount })}
          </span>
        ) : undefined
      }
      modalSettings={{
        hideCloseButton: true,
        width: 'm',
        subtitle,
        shouldCloseOnOverlayClick: !lockDismiss,
        shouldCloseOnEsc: !lockDismiss,
      }}
    />
  );
});
